import React, { useEffect, useState } from 'react';
import HomeModernIcon from 'mdi-react/HomeModernIcon';
import AccountMultipleIcon from 'mdi-react/AccountMultipleIcon';
import PulseIcon from 'mdi-react/PulseIcon';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';
import DisplayCounter from '../../_uiComponents/DisplayCounter';
import DisplayCounterSkeleton from '../../_uiComponents/DisplayCounterSkeleton';
import {
  getQuarterRange,
  isWithinRange,
  formatQoqPill,
} from '../../../util/quarterRange';

const d = partial('Deals');
const p = partial('CompanyListPage');
const s = partial('shared');

const qoqPill = (current, previous) =>
  formatQoqPill(current, previous, s('new'));

const CompanyStatisticsOverview = () => {
  const [loading, setLoading] = useState(true);
  const [activeCompanies, setActiveCompanies] = useState();
  const [companiesQoq, setCompaniesQoq] = useState();
  const [connectedEmployees, setConnectedEmployees] = useState();
  const [totalEmployees, setTotalEmployees] = useState();
  const [dealsCount, setDealsCount] = useState();
  const [dealsQoq, setDealsQoq] = useState();

  useEffect(() => {
    const range = getQuarterRange(1);
    const prevRange = getQuarterRange(2);

    const companiesPromise = Api.companies
      .getClubCompanies()
      .then((companies) => {
        const active = (companies || []).filter(
          (company) => company?.isAppVisible && company?.isSponsor
        );
        setActiveCompanies(active.length);
        const createdInQuarter = active.filter((company) =>
          isWithinRange(company?.createdAt, range.start, range.end)
        ).length;
        const createdInPrevQuarter = active.filter((company) =>
          isWithinRange(company?.createdAt, prevRange.start, prevRange.end)
        ).length;
        setCompaniesQoq(qoqPill(createdInQuarter, createdInPrevQuarter));
      })
      .catch(() => undefined);

    const employeesPromise = Api.companies
      .getAllCompaniesEmployees()
      .then(({ data }) => {
        setConnectedEmployees(data.filter((emp) => emp.userId).length);
        setTotalEmployees(data.length);
      })
      .catch(() => undefined);

    const dealsPromise = Api.companies
      .getDealStatistics({})
      .then((data) => setDealsCount(data.dealsCount))
      .catch(() => undefined);

    const dealsQoqPromise = Promise.all([
      Api.companies.getDealStatistics({
        startDate: range.startISO,
        endDate: range.endISO,
      }),
      Api.companies.getDealStatistics({
        startDate: prevRange.startISO,
        endDate: prevRange.endISO,
      }),
    ])
      .then(([current, previous]) => {
        setDealsQoq(
          qoqPill(current?.dealsCount || 0, previous?.dealsCount || 0)
        );
      })
      .catch(() => undefined);

    Promise.allSettled([
      companiesPromise,
      employeesPromise,
      dealsPromise,
      dealsQoqPromise,
    ]).then(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="my-2">
        <DisplayCounterSkeleton count={3} />
      </div>
    );
  }

  return (
    <div className="my-2">
      <DisplayCounter
        id="active-companies"
        title={d('activeCompanies')}
        text={d('inNetwork')}
        value={`${activeCompanies ?? 0}`}
        icon={<HomeModernIcon style={{ width: 18 }} />}
        pill={companiesQoq}
      />
      <DisplayCounter
        id="connected-employees"
        title={p('connectedEmployees')}
        text={p('outOfEmployees', { count: totalEmployees ?? 0 })}
        value={`${connectedEmployees ?? 0}`}
        icon={<AccountMultipleIcon style={{ width: 18 }} />}
      />
      <DisplayCounter
        id="total-activities"
        title={d('totalActivities')}
        text={d('acrossAllCompanies')}
        value={`${dealsCount ?? 0}`}
        icon={<PulseIcon style={{ width: 18 }} />}
        pill={dealsQoq}
      />
    </div>
  );
};

export default CompanyStatisticsOverview;
